import React, { useState, useEffect } from 'react';
import DateOfBirthPicker from '../../../components/DateOfBirthPicker';
import type { DoctorProfile, CountryCode, Timezone, GenderType } from '../../../types/doctorOnboarding';

type Props = {
  profile: DoctorProfile | null;
  countryCodes: CountryCode[];
  timezones: Timezone[];
  genderTypes: GenderType[];
  onSave: (personal: Partial<DoctorProfile>) => Promise<void>;
  saving: boolean;
  onBack: () => void;
};

const nameRegex = /^[a-zA-Z\s'-]+$/;

const TITLE_OPTIONS = ['', 'Dr', 'Mr', 'Ms', 'Mrs', 'Prof', 'Miss'];

export default function PersonalInfoStep({
  profile,
  countryCodes,
  timezones,
  genderTypes,
  onSave,
  saving,
  onBack,
}: Props) {
  const [title, setTitle] = useState(profile?.title ?? '');
  const [firstName, setFirstName] = useState(profile?.first_name ?? '');
  const [middleName, setMiddleName] = useState(profile?.middle_name ?? '');
  const [lastName, setLastName] = useState(profile?.last_name ?? '');
  const [dateOfBirth, setDateOfBirth] = useState(
    profile?.date_of_birth ? profile.date_of_birth.toString().slice(0, 10) : ''
  );
  const [gender, setGender] = useState(profile?.gender ?? '');
  const [contactNumber, setContactNumber] = useState(profile?.contact_number ?? '');
  const [countryCodeId, setCountryCodeId] = useState<number | ''>(profile?.country_code_id ?? '');
  const [timezoneId, setTimezoneId] = useState<number | ''>(profile?.timezone_id ?? '');
  const [addressLine1, setAddressLine1] = useState(profile?.address_line1 ?? '');
  const [addressLine2, setAddressLine2] = useState(profile?.address_line2 ?? '');
  const [city, setCity] = useState(profile?.city ?? '');
  const [state, setState] = useState(profile?.state ?? '');
  const [country, setCountry] = useState(profile?.country ?? '');
  const [pincode, setPincode] = useState(profile?.pincode ?? '');

  useEffect(() => {
    if (!profile) return;
    setTitle(profile.title ?? '');
    setFirstName(profile.first_name ?? '');
    setMiddleName(profile.middle_name ?? '');
    setLastName(profile.last_name ?? '');
    setDateOfBirth(
      profile.date_of_birth ? String(profile.date_of_birth).slice(0, 10) : ''
    );
    setGender((profile.gender ?? '').toLowerCase());
    setContactNumber(profile.contact_number ?? '');
    setCountryCodeId(profile.country_code_id ?? '');
    setTimezoneId(profile.timezone_id ?? '');
    setAddressLine1(profile.address_line1 ?? '');
    setAddressLine2(profile.address_line2 ?? '');
    setCity(profile.city ?? '');
    setState(profile.state ?? '');
    setCountry(profile.country ?? '');
    setPincode(profile.pincode ?? '');
  }, [profile]);

  useEffect(() => {
    if (countryCodeId !== '' || countryCodes.length === 0) return;
    const india =
      countryCodes.find((c) => (c.dial_code || '').trim() === '+91') ||
      countryCodes.find((c) => (c.iso2 || c.country_code || '').toUpperCase() === 'IN');
    if (india) {
      setCountryCodeId(india.id);
      setCountry(india.country_name);
      return;
    }
    const locale = (navigator.language || '').split('-')[1]?.toUpperCase();
    if (!locale) return;
    const match = countryCodes.find(
      (c) => (c.iso2 || c.country_code || '').toUpperCase() === locale
    );
    if (match) {
      setCountryCodeId(match.id);
      setCountry(match.country_name);
    }
  }, [countryCodes, countryCodeId]);

  useEffect(() => {
    if (timezoneId !== '' || timezones.length === 0) return;
    const kolkata = timezones.find((tz) => tz.identifier === 'Asia/Kolkata');
    if (kolkata) {
      setTimezoneId(kolkata.id);
      return;
    }
    const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browserTz) return;
    const match = timezones.find((tz) => tz.identifier === browserTz);
    if (match) {
      setTimezoneId(match.id);
    }
  }, [timezones, timezoneId]);

  const selectedCountry = countryCodes.find((c) => c.id === Number(countryCodeId));
  const phoneDigits = contactNumber.replace(/\D/g, '');
  const phoneValid = selectedCountry
    ? phoneDigits.length >= selectedCountry.min_digits &&
      phoneDigits.length <= selectedCountry.max_digits &&
      /^\d+$/.test(phoneDigits)
    : false;

  const dobDate = dateOfBirth ? new Date(dateOfBirth) : null;
  const age = dobDate
    ? Math.floor(
        (Date.now() - dobDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000)
      )
    : 0;
  const ageValid = age >= 22 && age <= 80;

  const today = new Date();
  const maxDob = new Date(today.getFullYear() - 22, today.getMonth(), today.getDate());
  const minDob = new Date(today.getFullYear() - 80, today.getMonth(), today.getDate());

  const firstNameValid =
    firstName.trim().length >= 2 &&
    firstName.trim().length <= 50 &&
    nameRegex.test(firstName.trim());
  const lastNameValid =
    lastName.trim().length >= 2 &&
    lastName.trim().length <= 50 &&
    nameRegex.test(lastName.trim());
  const pincodeClean = pincode.replace(/\D/g, '');
  const pincodeValid =
    pincodeClean.length === 0 || (pincodeClean.length >= 4 && pincodeClean.length <= 10);

  const genderValid =
    gender &&
    genderTypes.some(
      (g) => g.name.toLowerCase() === gender.toLowerCase()
    );
  const isValid =
    firstNameValid &&
    lastNameValid &&
    genderValid &&
    phoneValid &&
    selectedCountry &&
    timezoneId !== '' &&
    ageValid &&
    city.trim() !== '' &&
    state.trim() !== '' &&
    country.trim() !== '' &&
    pincodeValid;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid || saving) return;
    onSave({
      title: title.trim() || undefined,
      first_name: firstName.trim(),
      middle_name: middleName.trim() || undefined,
      last_name: lastName.trim(),
      date_of_birth: dateOfBirth || undefined,
      gender: gender || undefined,
      contact_number: contactNumber.replace(/\D/g, ''),
      country_code_id:
        typeof countryCodeId === 'string' ? undefined : countryCodeId,
      timezone_id: typeof timezoneId === 'string' ? undefined : timezoneId,
      address_line1: addressLine1.trim() || undefined,
      address_line2: addressLine2.trim() || undefined,
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      country: country.trim() || undefined,
      pincode: pincodeClean || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="onboarding-section">
        <h3>Basic Information</h3>
        <div className="form-group">
          <label>Title</label>
          <select
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          >
            {TITLE_OPTIONS.map((opt) => (
              <option key={opt || 'none'} value={opt}>
                {opt || 'Select title (optional)'}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>First Name <span className="required">*</span></label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            placeholder="John"
            maxLength={50}
          />
          {firstName && !firstNameValid && (
            <span className="field-hint">
              At least 2 characters; letters, spaces, hyphens, apostrophes only
            </span>
          )}
        </div>
        <div className="form-group">
          <label>Middle Name</label>
          <input
            value={middleName}
            onChange={(e) => setMiddleName(e.target.value)}
            placeholder="Optional"
            maxLength={100}
          />
        </div>
        <div className="form-group">
          <label>Last Name <span className="required">*</span></label>
          <input
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            placeholder="Doe"
            maxLength={50}
          />
          {lastName && !lastNameValid && (
            <span className="field-hint">
              At least 2 characters; letters, spaces, hyphens, apostrophes only
            </span>
          )}
        </div>
        <div className="form-group">
          <DateOfBirthPicker
            label="Date of Birth"
            value={dateOfBirth || undefined}
            onChange={(date) => setDateOfBirth(date ?? '')}
            minDate={minDob}
            maxDate={maxDob}
            required
            error={!!dateOfBirth && !ageValid}
          />
          {dateOfBirth && !ageValid && (
            <span className="field-hint">
              Doctor must be 22–80 years old (current age: {age})
            </span>
          )}
        </div>
        <div className="form-group">
          <label>Timezone <span className="required">*</span></label>
          <select
            value={timezoneId === '' ? '' : timezoneId}
            onChange={(e) => setTimezoneId(e.target.value === '' ? '' : Number(e.target.value))}
          >
            <option value="">Select timezone</option>
            {timezones
              .filter((t) => t.is_active)
              .map((t) => (
                <option key={t.id} value={t.id}>
                  {t.display_name}
                </option>
              ))}
          </select>
        </div>
        <div className="form-group">
          <label>Gender <span className="required">*</span></label>
          <select value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">Select gender</option>
            {genderTypes
              .filter((g) => g.is_active)
              .map((g) => (
                <option key={g.id} value={g.name.toLowerCase()}>
                  {g.name}
                </option>
              ))}
          </select>
        </div>
      </div>

      <div className="onboarding-section">
        <h3>Contact Information</h3>
        <div className="form-group">
          <label>Country Code <span className="required">*</span></label>
          <select
            value={countryCodeId === '' ? '' : countryCodeId}
            onChange={(e) => {
              const val = e.target.value === '' ? '' : Number(e.target.value);
              setCountryCodeId(val);
              const sel = countryCodes.find((c) => c.id === (val === '' ? 0 : val));
              if (sel) setCountry(sel.country_name);
            }}
          >
            <option value="">Select country</option>
            {countryCodes
              .filter((c) => c.is_active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.country_name} ({c.dial_code})
                </option>
              ))}
          </select>
        </div>
        <div className="form-group">
          <label>Contact Number <span className="required">*</span></label>
          <input
            type="tel"
            value={contactNumber}
            onChange={(e) => setContactNumber(e.target.value.replace(/\D/g, '').slice(0, 15))}
            placeholder="9876543210"
          />
          {selectedCountry && contactNumber && !phoneValid && (
            <span className="field-hint">
              {selectedCountry.country_name}: {selectedCountry.min_digits}–
              {selectedCountry.max_digits} digits
            </span>
          )}
        </div>
      </div>

      <div className="onboarding-section">
        <h3>Address</h3>
        <div className="form-group">
          <label>Address Line 1</label>
          <input
            value={addressLine1}
            onChange={(e) => setAddressLine1(e.target.value)}
            placeholder="Street address"
          />
        </div>
        <div className="form-group">
          <label>Address Line 2</label>
          <input
            value={addressLine2}
            onChange={(e) => setAddressLine2(e.target.value)}
            placeholder="Apartment, suite, etc."
          />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>City <span className="required">*</span></label>
            <input
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="Mumbai"
            />
          </div>
          <div className="form-group">
            <label>State <span className="required">*</span></label>
            <input
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder="Maharashtra"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Country <span className="required">*</span></label>
            <input
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              placeholder="India"
            />
          </div>
          <div className="form-group">
            <label>Pincode</label>
            <input
              value={pincode}
              onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 10))}
              placeholder="400001"
            />
            {pincode && !pincodeValid && (
              <span className="field-hint">4–10 digits</span>
            )}
          </div>
        </div>
      </div>

      <div className="step-actions">
        <button type="button" className="btn-secondary" onClick={onBack}>
          Back
        </button>
        <button type="submit" className="btn-primary" disabled={!isValid || saving}>
          {saving ? 'Saving...' : 'Save & Continue'}
        </button>
      </div>
    </form>
  );
}
